import { Link } from "react-router";
import {
  ArrowRight,
  Calendar,
  CheckCircle2,
  DollarSign,
  Gift,
  Package,
  ShieldCheck,
  Ticket,
  Wallet,
} from "lucide-react";
import {
  useCustomerCredits,
  useCustomerCreditsSummary,
} from "@/api/use-customer-credits";
import type { CustomerCredit } from "@/api.types.v2";
import { Button, Card, Input } from "@heroui/react";
import { PageHeader, IndigoBlock } from "@/components/page-chrome";
import { StatusBadge, type StatusTone } from "@/components/status-badge";
import { useTranslation } from "react-i18next";

/**
 * #262 — customer voucher wallet.
 *
 * Two voucher kinds:
 *   - CREDIT          → dollar amount applied to credit balance
 *   - FREE_LINE_ITEM  → covers a specific line (free pickup / shipment up to $X)
 *
 * Surfaces: Available + Recently used (server doesn't return expired rows
 * in `available`, so the design's third "Expired" group is omitted until
 * the API exposes it).
 */
export default function VouchersScreen() {
  const { t } = useTranslation("app");
  const { data: available, isLoading: loadingAvailable } =
    useCustomerCredits("available");
  const { data: used, isLoading: loadingUsed } = useCustomerCredits("redeemed");
  const { data: summary } = useCustomerCreditsSummary();

  const isLoading = loadingAvailable || loadingUsed;
  const hasAvailable = (available?.length ?? 0) > 0;
  const hasUsed = (used?.length ?? 0) > 0;

  const balanceCents = summary?.amountCents ?? 0;
  const creditVouchers =
    available?.filter((c) => c.kind === "AMOUNT").length ?? 0;
  const expiringCount =
    available?.filter((c) => isExpiringSoon(c.validUntil)).length ?? 0;

  return (
    <div className="space-y-9">
      <PageHeader
        eyebrow={t("vouchers.eyebrow")}
        title={t("vouchers.title")}
        italic={t("vouchers.italic")}
        lede={t("vouchers.lede")}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-4">
        <IndigoBlock className="rounded-[20px] p-8 flex flex-col">
          <div className="text-tiny font-semibold uppercase tracking-wider text-white/70 mb-3">
            {t("vouchers.creditBalance")}
          </div>
          <div className="text-[48px] font-mono font-bold leading-none tracking-[-0.03em]">
            {formatAmountCents(balanceCents)}
          </div>
          <div className="text-sm text-white/85 mt-3.5 leading-normal max-w-[420px]">
            {creditVouchers > 0
              ? t("vouchers.creditVouchers", {
                  count: creditVouchers,
                })
              : t("vouchers.creditEmpty")}
          </div>
          <div className="flex items-baseline gap-5 mt-auto pt-6">
            <div>
              <div className="text-2xl font-mono font-bold">
                {available?.length ?? 0}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-white/70 mt-0.5">
                {t("vouchers.available")}
              </div>
            </div>
            <div className="w-px self-stretch bg-white/20" />
            <div>
              <div className="text-2xl font-mono font-bold">
                {expiringCount}
              </div>
              <div className="text-[11px] uppercase tracking-wider text-white/70 mt-0.5">
                {t("vouchers.expiringSoon")}
              </div>
            </div>
          </div>
        </IndigoBlock>

        <div className="bg-content1 rounded-[20px] p-7 shadow-small flex flex-col">
          <div className="text-base font-bold">{t("vouchers.haveCode")}</div>
          <div className="text-sm text-default-500 mt-1 leading-snug">
            {t("vouchers.codeBody")}
          </div>
          <div className="mt-auto pt-5">
            <Input
              type="text"
              placeholder={t("vouchers.codePlaceholder")}
              variant="bordered"
              startContent={<Ticket className="h-4 w-4 text-default-400" />}
              endContent={
                <Button size="sm" color="primary" isDisabled>
                  {t("vouchers.redeem")}
                </Button>
              }
              classNames={{
                input: "font-mono tracking-wider",
                inputWrapper: "h-12 pr-1",
              }}
              isDisabled
            />
            <div className="mt-2.5 flex items-center gap-1.5 text-xs text-default-500">
              <ShieldCheck className="h-3 w-3 text-default-400" />
              {t("vouchers.redemptionSoon")}
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-default-500 py-4">{t("vouchers.loading")}</div>
      ) : !hasAvailable && !hasUsed ? (
        <EmptyState />
      ) : (
        <>
          {hasAvailable && (
            <VoucherGroup
              title={t("vouchers.availableGroup")}
              count={available!.length}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {available!.map((credit) => (
                  <VoucherCard key={credit.id} credit={credit} />
                ))}
              </div>
            </VoucherGroup>
          )}

          {hasUsed && (
            <VoucherGroup
              title={t("vouchers.recentlyUsed")}
              count={used!.length}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {used!.slice(0, 10).map((credit) => (
                  <VoucherCard key={credit.id} credit={credit} muted />
                ))}
              </div>
            </VoucherGroup>
          )}
        </>
      )}
    </div>
  );
}

function VoucherGroup({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  const { t } = useTranslation("app");

  return (
    <section className="space-y-4">
      <div className="flex items-baseline gap-2.5">
        <h2 className="text-lg font-bold tracking-tight m-0">{title}</h2>
        <span className="text-sm text-default-500">
          {t("vouchers.voucherCount", { count })}
        </span>
      </div>
      {children}
    </section>
  );
}

function VoucherCard({
  credit,
  muted,
}: {
  credit: CustomerCredit;
  muted?: boolean;
}) {
  const { t } = useTranslation("app");
  const isCredit = credit.kind === "AMOUNT";
  const isRedeemed = !!credit.redeemedAt;
  const expiring = !isRedeemed && isExpiringSoon(credit.validUntil);

  const railColor = isRedeemed
    ? "bg-default-300"
    : expiring
      ? "bg-warning-500"
      : isCredit
        ? "bg-primary"
        : "bg-success-600";

  const tileBg = isRedeemed
    ? "bg-default-100 text-default-500"
    : isCredit
      ? "bg-primary-50 text-primary"
      : "bg-success-50 text-success-700";

  const chipTone: StatusTone = isRedeemed
    ? "default"
    : expiring
      ? "warning"
      : "success";
  const chipLabel = isRedeemed
    ? t("vouchers.redeemed")
    : expiring
      ? expiringLabel(credit.validUntil, t)
      : t("vouchers.statusAvailable");

  const title = voucherTitle(credit, t);
  const value = voucherValue(credit, t);
  const code = credit.id.slice(0, 8).toUpperCase();
  const metaLabel = isRedeemed
    ? credit.redeemedOrder?.orderNumber
      ? t("vouchers.redeemedWithOrder", {
          date: formatDate(credit.redeemedAt!),
          orderNumber: credit.redeemedOrder.orderNumber,
        })
      : t("vouchers.redeemedDate", { date: formatDate(credit.redeemedAt!) })
    : credit.validUntil
      ? t("vouchers.expires", { date: formatDate(credit.validUntil) })
      : t("vouchers.noExpiry");

  return (
    <div
      className={`flex rounded-large overflow-hidden bg-content1 shadow-small ${
        muted || isRedeemed ? "opacity-70" : ""
      }`}
    >
      <div className={`w-1.5 flex-shrink-0 ${railColor}`} />
      <div className="flex-1 p-5 min-w-0">
        <div className="flex items-start gap-3.5">
          <span
            className={`w-11 h-11 rounded-medium flex-shrink-0 grid place-items-center ${tileBg}`}
          >
            {isCredit ? (
              <Wallet className="h-5 w-5" />
            ) : (
              <Ticket className="h-5 w-5" />
            )}
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold tracking-tight">{title}</span>
              <StatusBadge tone={chipTone} className="ml-auto flex-shrink-0">
                {chipLabel}
              </StatusBadge>
            </div>
            <div className="text-sm text-default-600 mt-1 leading-snug">
              {credit.reason}
            </div>

            <div className="flex items-center gap-3.5 mt-3.5 flex-wrap text-xs text-default-500">
              <span className="font-mono px-2 py-0.5 rounded bg-default-100 text-default-700 tracking-wider">
                {code}
              </span>
              <span className="inline-flex items-center gap-1">
                {isRedeemed ? (
                  <CheckCircle2 className="h-3 w-3" />
                ) : (
                  <Calendar className="h-3 w-3" />
                )}
                {metaLabel}
              </span>
              <span className="inline-flex items-center gap-1">
                {isCredit ? (
                  <DollarSign className="h-3 w-3" />
                ) : (
                  <Package className="h-3 w-3" />
                )}
                {value}
              </span>
            </div>
          </div>
        </div>

        {!isRedeemed && (
          <div className="mt-4 pt-3.5 border-t border-divider flex items-center justify-between gap-3">
            <span className="text-xs text-default-500">
              {isCredit
                ? t("vouchers.creditApplied")
                : t("vouchers.lineApplied")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation("app");

  return (
    <Card shadow="none" className="border border-divider">
      <div className="py-12 text-center px-6">
        <Gift className="h-10 w-10 text-primary/30 mx-auto mb-3" />
        <h2 className="text-base font-semibold text-foreground">
          {t("vouchers.emptyTitle")}
        </h2>
        <p className="text-sm text-default-500 mt-2 max-w-md mx-auto">
          {t("vouchers.emptyBody")}
        </p>
        <Button
          as={Link}
          to="/settings"
          variant="bordered"
          size="sm"
          className="mt-5"
          endContent={<ArrowRight className="h-3.5 w-3.5" />}
        >
          {t("vouchers.viewMembership")}
        </Button>
      </div>
    </Card>
  );
}

function voucherTitle(
  credit: CustomerCredit,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  if (credit.kind === "AMOUNT") {
    return t("vouchers.creditTitle", {
      amount: formatAmountCents(credit.amountCents ?? 0),
    });
  }
  if (credit.scope === "PICKUP") return t("vouchers.freePickup");
  if (credit.scope === "DELIVERY")
    return credit.amountCents
      ? t("vouchers.freeDeliveryUpTo", {
          amount: formatAmountCents(credit.amountCents),
        })
      : t("vouchers.freeDelivery");
  return t("vouchers.freeLineItem");
}

function voucherValue(
  credit: CustomerCredit,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  if (credit.kind === "AMOUNT")
    return t("vouchers.value", {
      amount: formatAmountCents(credit.amountCents ?? 0),
    });
  if (credit.redeemedAt && credit.redemptionCents)
    return t("vouchers.saved", {
      amount: formatAmountCents(credit.redemptionCents),
    });
  return credit.amountCents
    ? t("vouchers.upTo", { amount: formatAmountCents(credit.amountCents) })
    : t("vouchers.fullLine");
}

function isExpiringSoon(validUntil: string | null): boolean {
  if (!validUntil) return false;
  const days =
    (new Date(validUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return days > 0 && days <= 14;
}

function expiringLabel(
  validUntil: string | null,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  if (!validUntil) return t("vouchers.expiringSoon");
  const days = Math.ceil(
    (new Date(validUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
  return t("vouchers.expiresIn", { count: days });
}

function formatAmountCents(cents: number): string {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(iso));
}
